import { Tooltip } from '@/components/ui/tooltip';
import {
  countLabel,
  retiredCriterionSentence,
  stepTitle,
  unscoredThresholdSentence,
} from '@/lib/protocol-labels';
import { cn } from '@/lib/utils';
import type { ProtocolCard, ProtocolCardStep } from '@matter_extractor/shared';
import { TriangleAlert } from 'lucide-react';

/**
 * The two blocks a protocol's card adds to the common tile (refonte modulaire
 * §7.1, §15; maquette `editeur-protocole.html`, `cardHTML`).
 *
 * §15 puts the mini-chain of the steps under the name and the two counts in the
 * line of facts. Neither the chain nor the warning line is a sentence, so both
 * ride the two slots `versioned-card.tsx` grew for the persona in #42 -
 * `renderIdentity` beside the chevron, `renderExtra` between that row and the
 * pastilles. The layer keeps everything the four objects share: the pastilles,
 * the usage, the stamp, the greyed-out state of a retired card and the link that
 * wraps it all.
 *
 * ## Why the chain is folded, and folded THAT way
 *
 * « Les deux premières, une ellipse verticale, la dernière » (§7.1). A protocol
 * has nine steps in the mockup and fifty at the schema's ceiling, and a card
 * that listed them all would be a card the length of a column - so the grid
 * would stop being scannable, which is the whole point of a library. Three or
 * fewer are shown WHOLE: folding two steps into « two, ellipsis, one » would
 * hide nothing and cost a row.
 *
 * ## The full list is a PANEL, and it opens on the CHAIN alone
 *
 * §7.1 asks for « une grande infobulle » listing every step at hover, and until
 * issue #104 that infobulle was the native `title`. It can no longer be: a
 * `title` is drawn by the BROWSER, so on a machine in dark theme it comes out
 * white on black and no CSS of this repository reaches it. `ui/tooltip.tsx`
 * draws the bubble itself - white, portalled, never cropped by an ancestor's
 * overflow - and this chain carried the last native `title` of the sub-tree.
 *
 * The panel hangs off the CHAIN and never off the card. A library of protocols
 * is a grid one sweeps with the eyes, and a panel armed anywhere on a card
 * would light up all along the path of the pointer, on every card crossed on
 * the way to the one actually aimed at. A chain is a thin target, though, so its
 * box is widened by a padding on the sides and taken straight back by an equal
 * negative margin: the sensitive zone gains 8 px on each side and nothing on the
 * card moved. The nodes keep their width for free - `max-w-full` resolves
 * against the CONTENT box, which the padding restored to exactly what it was.
 *
 * ## What the panel does NOT say
 *
 * No header of counts. How many steps and how many jumps are already two facets
 * of the line of facts, a few rows under the chain, and a panel that repeats its
 * own card is noise. The mockup's `.tip-title` is therefore dropped on purpose
 * (grill of 2026-09-21) rather than forgotten on the way.
 *
 * ## It exists only while the chain is FOLDED
 *
 * Unfolded, the three nodes already say everything the panel would, and a second
 * copy of a list one can read without moving is a surface that lights up for
 * nothing. That is the rule the `sr-only` list already followed, for its own
 * reason; both now hang off the same `folded`.
 *
 * ## Twelve rows, and the last one counts what it hides
 *
 * Fifty steps at the schema's ceiling would be a panel taller than the window,
 * which the placer would then clamp over the very card it points at. So the
 * panel stops at `PANEL_MAX_STEPS` and says what it dropped - « et 38 autres
 * étapes ». A list may say how much of itself it hides; it may not hide it
 * quietly.
 *
 * ## The chain stays UNFOCUSABLE, and the `sr-only` list stays beside it
 *
 * The chain lives inside the card's link. A focusable span in there would be a
 * second tab stop on every card of the grid and a trap to get back out of, so
 * the chain takes no focus, the panel opens at hover only, and the keyboard's
 * answer remains the `sr-only` list - WHOLE where the panel is capped, because
 * a list that is spoken and not drawn costs no height to anybody.
 */

/**
 * How many steps the panel draws before it starts counting instead.
 *
 * A dozen rows is what still reads at a glance, and past it the thirteenth name
 * is worth less than knowing there are thirty-eight more behind it. The cut
 * fires at thirteen steps too, where it saves no row at all: « et 1 autre
 * étape » is the price of ONE rule instead of two, and a rule that excepts its
 * own boundary is a rule nobody can state.
 */
const PANEL_MAX_STEPS = 12;

/** One node: the rank in a small tab, then the name (§7.1). */
function ChainNode({ step, index }: { step: ProtocolCardStep; index: number }) {
  return (
    <span className="inline-flex max-w-full items-center overflow-hidden rounded-md border border-gray-300 bg-white text-gray-800 text-xs leading-5">
      {/* `tabular-nums` so a chain of nine steps keeps one left edge for its
          names: proportional digits move the second column by a hair per rank. */}
      <span className="shrink-0 bg-gray-100 px-2 font-semibold text-gray-500 tabular-nums">
        {index + 1}
      </span>
      <span className="truncate px-2">{step.name}</span>
    </span>
  );
}

/**
 * The 1 px rule between two nodes, aligned under the tab rather than centred.
 *
 * `bg-rail` and not a bare grey utility, for the reason `persona-card`'s gauge
 * gives: the charte forbids grey from `200` up as a SURFACE and the mechanical
 * guard refuses the raw class, but a hairline between two tabs is neither a
 * surface nor a border - so it carries its own token, declared in `styles.css`.
 */
function ChainLink() {
  return <span aria-hidden="true" className="ml-4 block h-3 w-px bg-rail" />;
}

/**
 * What the panel holds: one row per step, the rank then the name (maquette
 * `editeur-protocole.html`, `.tip-row`).
 *
 * The width is the BUBBLE's, asked for by name. `Tooltip` caps a panel at 240 px
 * by default - the design system's figure, cut for a label like « Se déconnecter »
 * - and this one carries a list of names, which the mockup draws at 280. A first
 * pass tried to widen it with a class on the content and found out why that
 * cannot work: the cap is an inline style on the bubble, and a class never beats
 * one. So the component took a `maxWidth` instead, the twenty-eight generic
 * sites keep the default, and this one says what it needs.
 *
 * What it draws is the head of the chain, what is missing, then the TAIL - the
 * same fold the card itself uses, so the step the card singles out is in the
 * panel that explains it.
 */
/** The bubble's own width. The mockup's `.tip` draws 280. */
const CHAIN_PANEL_WIDTH = 280;

/** One row: the rank in a fixed column, then the name. */
function ChainRow({ rank, name }: { readonly rank: number; readonly name: string }) {
  return (
    /* `items-baseline` where the mockup centres: at this width a long name
       wraps onto a second line, and a rank centred against the whole block
       would then sit beside neither of the two lines it numbers. */
    <div className="grid grid-cols-[1.25rem_minmax(0,1fr)] items-baseline gap-2 py-0.5">
      <span className="font-semibold text-gray-500 tabular-nums">{rank}</span>
      <span className="break-words">{name}</span>
    </div>
  );
}

function ChainPanel({ steps }: { steps: readonly ProtocolCardStep[] }) {
  /*
    The LAST step is always drawn, and that is a correction from review.

    A protocol of fifty steps draws « 1, 2, ⋮, 50 » on its card, and the panel
    that exists to explain that chain used to stop at twelve - so the one step
    the card singles out was the one the panel left out. It now folds the way
    the chain itself folds: the head, what is missing, then the tail.

    The cut only fires past `PANEL_MAX_STEPS + 1`: at exactly one over, hiding a
    single row to say « et 1 autre étape » would cost a line to save a line.
  */
  const folded = steps.length > PANEL_MAX_STEPS + 1;
  const head = folded ? steps.slice(0, PANEL_MAX_STEPS - 1) : steps;
  const hidden = steps.length - head.length - (folded ? 1 : 0);
  const last = steps[steps.length - 1];

  return (
    /* The inner width follows the bubble's, less the 10 px of padding it puts
       on each side: written once and derived, because the two used to be two
       numbers in two units kept in step by hand. Fixed rather than hugging its
       content, because the mockup fixes it too - a panel whose width followed
       its longest name would be a different shape on every card of one grid. */
    <div style={{ width: CHAIN_PANEL_WIDTH - 20 }}>
      {head.map((step, index) => (
        <ChainRow key={step.id} rank={index + 1} name={step.name} />
      ))}
      {folded && last !== undefined ? (
        <>
          {/* `pl-7` is the rank column plus its gap, so the count lines up with
              the NAMES above it and reads as one more of them - not as a footer
              hung under a column of numbers it has nothing to do with. */}
          <p className="py-0.5 pl-7 text-gray-500">
            et {countLabel(hidden, 'autre étape', 'autres étapes')}
          </p>
          <ChainRow rank={steps.length} name={last.name} />
        </>
      ) : null}
    </div>
  );
}

/**
 * What stands beside the chevron: the name, then the short description (§7.1,
 * « celle des cartes et de la page de choix d'un lien »).
 *
 * The `<h2>` keeps the layer's own classes rather than new ones, so a protocol's
 * card and a criterion's read as one register in a library somebody switches
 * between.
 */
export function ProtocolCardIdentity({ card }: { card: ProtocolCard }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <h2 className="min-w-0 break-words font-semibold text-bleu-nuit text-lg leading-7">
        {card.name}
      </h2>
      {card.description === '' ? null : (
        <p className="min-w-0 break-words text-caption text-gray-600">{card.description}</p>
      )}
    </div>
  );
}

/** The mini-chain, and under it one warning line per retired criterion (§4, §7.1). */
export function ProtocolCardChain({ card }: { card: ProtocolCard }) {
  const steps = card.steps;
  const folded = steps.length > 3;
  const shown = folded ? [steps[0], steps[1]] : steps;
  const last = steps[steps.length - 1];
  /* « 1 · Accueil, 2 · Contexte » - the whole chain, for the ear alone. */
  const spoken = steps.map((step, index) => stepTitle({ index, name: step.name })).join(', ');

  const chain = (
    <div
      data-testid="protocole-carte-chaine"
      /* See the header: the padding widens what the pointer may aim at, the
         negative margin hands the layout back the pixels it just took. Only
         while folded, because only a folded chain has a panel to arm. */
      className={cn('flex flex-col items-start', folded && '-mx-2 px-2')}
    >
      {shown.map((step, index) =>
        step === undefined ? null : (
          <span key={step.id} className="flex max-w-full flex-col items-start">
            {index === 0 ? null : <ChainLink />}
            <ChainNode step={step} index={index} />
          </span>
        ),
      )}
      {folded && last !== undefined ? (
        <>
          {/* The vertical ellipsis, and not « + 6 » : §7.1 asks for a shape
              that says « il y en a d'autres » without a number the eye then
              has to add to the two above it.

              The CHARACTER and not its numeric HTML entity: the charte's
              mechanical guard scans every source for a hash followed by hex
              digits, and reads that entity as a raw colour. */}
          <span aria-hidden="true" className="ml-2.5 block text-gray-400 text-lg leading-4">
            ⋮
          </span>
          <ChainLink />
          <ChainNode step={last} index={steps.length - 1} />
        </>
      ) : null}
    </div>
  );

  /*
    `side="right"`, the side the mockup gives its `.tip`: the panel steps out
    beside the card rather than over it. Above, it would cover the name of the
    very protocol being pointed at - and against the last column of the grid the
    placer flips it to the left on its own, which is what it is there for.
  */
  const armed = folded ? (
    <Tooltip
      label={<ChainPanel steps={steps} />}
      side="right"
      maxWidth={CHAIN_PANEL_WIDTH}
      data-testid="protocole-carte-chaine-panneau"
    >
      {chain}
    </Tooltip>
  ) : (
    chain
  );

  return (
    <div className="space-y-2">
      {steps.length === 0 ? <p className="text-gray-600 text-xs leading-4">aucune étape</p> : armed}

      {folded ? <span className="sr-only">{spoken}</span> : null}

      {card.retiredCriteria.map((entry) => (
        <p
          key={`${entry.criterionId}-${entry.stepIndex}-${entry.objectiveName ?? ''}`}
          data-testid="protocole-carte-avertissement"
          className="flex items-start gap-1.5 text-jaune-deep text-xs leading-4"
        >
          <TriangleAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" />
          <span>{retiredCriterionSentence(entry)}</span>
        </p>
      ))}

      {/*
        The SECOND warning line (§7.2, grill of 2026-09-16), drawn like the
        first and under it: a retired criterion is a citation that resolves to
        nothing, a threshold without notation is a citation that resolves and
        decides nothing. The order is the order of gravity, and both carry the
        same mark so a card with either reads the same way from across a screen.
      */}
      {card.unscoredThresholds.map((entry) => (
        <p
          key={`seuil-${entry.criterionId}-${entry.stepIndex}-${entry.objectiveName}`}
          data-testid="protocole-carte-avertissement"
          className="flex items-start gap-1.5 text-jaune-deep text-xs leading-4"
        >
          <TriangleAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" />
          <span>{unscoredThresholdSentence(entry)}</span>
        </p>
      ))}
    </div>
  );
}
